import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  type AdminPreferences,
  DEFAULT_PREFERENCES,
  PREFERENCES_COOKIE,
  serializePreferences,
} from "@/lib/preferences";

interface PreferencesContextValue {
  preferences: AdminPreferences;
  setPreference: <K extends keyof AdminPreferences>(key: K, value: AdminPreferences[K]) => void;
  reset: () => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({
  initial,
  children,
}: {
  initial: AdminPreferences;
  children: ReactNode;
}) {
  const [preferences, setPreferences] = useState(initial);

  // Mirror the content layout on <html> so CSS can centre the page.
  useEffect(() => {
    document.documentElement.dataset.contentLayout = preferences.contentLayout;
    document.documentElement.dataset.navbarStyle = preferences.navbarStyle;
  }, [preferences.contentLayout, preferences.navbarStyle]);

  const persist = useCallback((next: AdminPreferences) => {
    document.cookie = `${PREFERENCES_COOKIE}=${serializePreferences(next)}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
  }, []);

  const setPreference = useCallback<PreferencesContextValue["setPreference"]>(
    (key, value) => {
      setPreferences((current) => {
        const next = { ...current, [key]: value };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const reset = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
    persist(DEFAULT_PREFERENCES);
  }, [persist]);

  const value = useMemo(
    () => ({ preferences, setPreference, reset }),
    [preferences, setPreference, reset],
  );
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error("usePreferences must be used inside <PreferencesProvider>");
  return context;
}
